import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import {
  IconCircleArrow01,
  IconSquareArrow01,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { Swiper as SwiperClass } from 'swiper/types';
import * as S from './style';

interface IProps {
  children: React.ReactNode;
  menuBarOnSwiper: SwiperClass | null;
  childrenMenuBarRef: React.RefObject<HTMLDivElement | null>;
  itemsLength: number;
  /** 메뉴가 컨테이너를 넘치는지 여부. 측정은 CategoryMenuBar가 담당한다 */
  isOverflow?: boolean;
  type?:
    | 'type1'
    | 'type2'
    | 'type3'
    | 'type4'
    | 'type5'
    | 'type6'
    | 'type7'
    | 'type8' // 기본2
    | 'type9' // 기본3
    | 'type10' // 기본4
    | 'type11' // 기본5
    | 'type12'; // PC 구분선 박스형
  // type1: 기본, type2: square, type3: round, type4: text, type5: underBar, type6: round white, type7: underBar small, type8: 기본2, type9: 기본3, type10: 기본4, type11: 기본5
  className?: string;
  onClickFold?: () => void;
  isDesktop?: boolean;
}

const FoldMenuBar = ({
  children,
  menuBarOnSwiper,
  childrenMenuBarRef,
  onClickFold,
  className,
  itemsLength,
  isOverflow,
  isDesktop,
  type = 'type1',
}: IProps) => {
  const [childrenRef, setChildrenRef] = useState<HTMLDivElement | null>(null);
  const isClosingByItemRef = useRef(false);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldShowButton = isDesktop ? !!isOverflow : itemsLength >= 10;

  const menuBarClassName = classNameBind([
    'fold-menubar',
    shouldShowButton ? 'now-fold' : '',
  ]);

  const cName = classNameBind(['btn-folding', className || '']);

  useEffect(() => {
    setChildrenRef(childrenMenuBarRef.current);
  }, [childrenMenuBarRef, menuBarOnSwiper]);

  useEffect(() => {
    if (!childrenRef) return undefined;

    /** 접힌 레이아웃 기준으로 위치를 다시 재고 해당 슬라이드로 이동 */
    const moveToSlide = (slide: HTMLElement) => {
      if (!menuBarOnSwiper || menuBarOnSwiper.destroyed) return;

      const idx = menuBarOnSwiper.slides.indexOf(slide);
      if (idx === -1) return;

      menuBarOnSwiper.updateSize();
      menuBarOnSwiper.updateSlides();
      menuBarOnSwiper.wrapperEl.classList.toggle(
        'noMove',
        slide.offsetLeft < childrenRef.clientWidth / 2
      );
      menuBarOnSwiper.slideTo(idx);
    };

    const handleMenuItemClick = (e: Event) => {
      if (!childrenRef.classList.contains('unFolding')) return;

      childrenRef.classList.remove('unFolding');

      // 접히면서 발생하는 resize 재정렬은 건너뛰고 클릭한 항목으로 한 번만 이동시킨다
      isClosingByItemRef.current = true;
      moveToSlide(e.currentTarget as HTMLElement);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isClosingByItemRef.current = false;
        });
      });
    };

    const menuItems = childrenRef.querySelectorAll('.swiper-slide');
    menuItems.forEach(item => {
      item.addEventListener('click', handleMenuItemClick);
    });

    return () => {
      menuItems.forEach(item => {
        item.removeEventListener('click', handleMenuItemClick);
      });
    };
  }, [childrenRef, itemsLength, menuBarOnSwiper]);

  const btnCategoryfolding = useCallback(() => {
    if (childrenRef) {
      childrenRef.classList.toggle('unFolding');
    }
  }, [childrenRef]);

  const handleButtonClick = useCallback(() => {
    if (isDesktop) {
      btnCategoryfolding();
    } else if (onClickFold) {
      onClickFold();
    }
  }, [btnCategoryfolding, isDesktop, onClickFold]);

  useEffect(() => {
    if (menuBarOnSwiper && childrenRef) {
      const handleResize = (swiper: SwiperClass) => {
        if (isClosingByItemRef.current) return undefined;

        const idx = swiper.slides.findIndex((v: Element) =>
          v.querySelector('.active')
        );
        const slidesActive = swiper.slides[idx];
        if (!slidesActive) return undefined;

        if (slidesActive.offsetLeft < childrenRef.clientWidth / 2) {
          swiper.wrapperEl.classList.add('noMove');
        } else {
          swiper.wrapperEl.classList.remove('noMove');
          if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
          resizeTimerRef.current = setTimeout(() => {
            if (!swiper.destroyed) swiper.slideTo(idx);
          }, 10);
        }
        return undefined;
      };

      const handleSlideChange = (swiper: SwiperClass) => {
        const idx = swiper.slides.findIndex((v: Element) =>
          v.querySelector('.active')
        );
        const slidesActive = swiper.slides[idx];

        if (
          !childrenRef.classList.contains('unFolding') &&
          slidesActive?.offsetLeft > childrenRef.clientWidth / 2
        ) {
          swiper.wrapperEl.classList.remove('noMove');
        }
        return undefined;
      };

      menuBarOnSwiper.on('resize', handleResize);
      menuBarOnSwiper.on('slideChange', handleSlideChange);

      return () => {
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        menuBarOnSwiper.off('resize', handleResize);
        menuBarOnSwiper.off('slideChange', handleSlideChange);
      };
    }
    return undefined;
  }, [childrenRef, menuBarOnSwiper]);

  return (
    <S.FoldMenuBar className={menuBarClassName} type={type}>
      {children}

      {shouldShowButton ? (
        <Button className={cName} onClick={handleButtonClick}>
          {type === 'type1' && <IconCircleArrow01 />}
          {(type === 'type2' || type === 'type3') && <IconSquareArrow01 />}
        </Button>
      ) : null}
    </S.FoldMenuBar>
  );
};

export default memo(FoldMenuBar);
